import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@radix-ui/react-icons';
import { Card, CardContent, Badge, Button } from '../../ui';
import { ROUTE_PATTERNS, buildRoute } from '../../../constants/Paths';
import { STATUS_LABEL } from './pageState';
import { reviewIsActive } from './useHeartbeatContext';

/**
 * `activeRunId`, when given, is the run currently in flight (queued /
 * collecting / labeling). Retrying it makes no sense, so its retry control
 * stays disabled even on the rare shape where a run is both "active" and
 * shows a failed replies count. In practice an active run's own status is
 * never 'partial' or 'failed', so this is a belt-and-suspenders guard for
 * the case that matters: history is now visible (and its earlier, terminal
 * runs' retry buttons stay live) while a newer run is still listening.
 *
 * Every row is a link to that run. The rows used to be plain list items,
 * because the account page rendered only the newest terminal run and there was
 * nowhere for an older one to lead - so a pulse you could see listed was a
 * pulse you could not open. Retry stays a button, and sits outside the link so
 * it does not navigate.
 */
export function RunHistory({ runs = [], onRetry, retrying, activeRunId, accountId }) {
	if (!runs.length) return null;
	return (
		<Card>
			<CardContent className="p-0">
				<ul className="divide-y divide-border">
					{runs.map((r) => {
						const canRetry = (r.status === 'partial' || r.status === 'failed') && r.replies?.failed > 0;
						const reviewing = reviewIsActive(r.contextReview);
						return (
							<li key={r._id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
								<Link
									to={buildRoute(ROUTE_PATTERNS.HEARTBEAT_RUN, { accountId, runId: r._id })}
									className="group flex min-w-0 flex-1 flex-wrap items-center gap-3 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600"
								>
									<span className="text-foreground group-hover:text-brand-700 group-hover:underline">
										{new Date(r.createdAt).toLocaleString()}
									</span>
									<Badge variant="outline" size="sm">{STATUS_LABEL[r.status] || r.status}</Badge>
									<span className="text-muted-foreground">{r.replies?.collected || 0} replies · {r.targetsCount || 0} posts</span>
									{r.status === 'partial' && (
										<span className="text-amber-700">
											Some posts couldn't be read{r.replies?.failed ? ` · ${r.replies.failed} replies we couldn't read` : ''}
										</span>
									)}
									{r.status === 'failed' && (
										<span className="text-red-700">{r.error || "We couldn't read this account's replies"}</span>
									)}
									<ChevronRightIcon className="ml-auto h-4 w-4 shrink-0 text-muted-foreground group-hover:text-brand-600" />
								</Link>
								{canRetry && (
									<Button
										size="sm"
										variant="outline"
										onClick={() => onRetry(r._id)}
										disabled={retrying || reviewing || r._id === activeRunId}
									>
										{reviewing ? 'Reviewing replies...' : 'Retry reading'}
									</Button>
								)}
							</li>
						);
					})}
				</ul>
			</CardContent>
		</Card>
	);
}
